import { Avatar, Button, Card, CardBody, CardFooter, CardHeader, Skeleton } from "@heroui/react";
import { Icon } from "@iconify/react";
import * as React from "react";
import useAuthStore from "../store/authStore";
import { useApi } from "../hooks/useApi";
import { Subscription } from "../../../types";
import { useTranslation } from "react-i18next";

export const UserCard = () => {
  const [activeSubscription, setActiveSubscription] = React.useState<Subscription | null>(null);
  const { logout } = useAuthStore();
  const { loading, get } = useApi();
  const { t, i18n } = useTranslation();

  const switchLanguage = () => {
    const nextLanguage = i18n.language === "mn" ? "en" : "mn";
    i18n.changeLanguage(nextLanguage);
  };

  const getLanguageText = () => {
    return i18n.language === "mn" ? "EN" : "MN";
  };

  const getActiveSubscription = async () => {
    const data = (await get("/active-subscription")) as Subscription;
    setActiveSubscription(data);
  };

  const switchPlan = () => {
    window.open("https://buy.bolorsoft.com/#tab-3", "_blank");
  };

  React.useEffect(() => {
    getActiveSubscription();
  }, []);

  return (
    <Card className="w-[300px] border-none bg-transparent" shadow="none">
      <CardHeader className="justify-between">
        <div className="flex gap-3">
          {loading ? (
            <Skeleton className="h-12 w-12 rounded-full" />
          ) : (
            <Avatar
              fallback={<Icon width={20} height={20} icon="clarity:avatar-line" />}
              isBordered
              name={activeSubscription ? activeSubscription.user.display_name : "User"}
            />
          )}
          <div className="flex flex-col items-start justify-center">
            {loading ? (
              <>
                <Skeleton className="h-4 w-24 rounded-md" />
                <Skeleton className="mt-1 h-3 w-32 rounded-md" />
              </>
            ) : (
              <>
                <h4 className="text-small font-semibold leading-none text-default-600">
                  {activeSubscription?.user.display_name || ""}
                </h4>
                <h5 className="text-small tracking-tight text-default-500">
                  {activeSubscription?.user.email || ""}
                </h5>
              </>
            )}
          </div>
        </div>

        <Button
          size="sm"
          variant="light"
          radius="full"
          className="text-default-500 hover:text-default-700 min-w-unit-12"
          onPress={switchLanguage}
          title="Switch Language"
        >
          <span className="text-small font-medium">{getLanguageText()}</span>
        </Button>
      </CardHeader>

      <CardBody className="px-3 py-0">
        {loading ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-40 rounded-md" />
            <Skeleton className="h-4 w-32 rounded-md" />
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            <p className="text-small text-default-500">
              {t("activePlan")}:{" "}
              <span className="font-medium text-default-600">
                {activeSubscription?.plan.name || ""}
              </span>
            </p>
            <p className="text-small text-default-500">
              {t("expiryDate")}:{" "}
              <span className="font-medium text-default-600">
                {activeSubscription?.end_date
                  ? new Intl.DateTimeFormat(i18n.language === "mn" ? "mn-MN" : "en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    }).format(new Date(activeSubscription.end_date))
                  : ""}
              </span>
            </p>
          </div>
        )}
      </CardBody>

      <CardFooter className="flex flex-col gap-3">
        {activeSubscription?.plan.name === "Free" && (
          <Button
            className="w-full"
            radius="full"
            size="sm"
            color="secondary"
            variant="bordered"
            onPress={() => switchPlan()}
          >
            {t("upgradePlan")}
          </Button>
        )}
        <Button
          className="w-full"
          color="primary"
          radius="full"
          size="sm"
          variant="solid"
          onPress={() => logout()}
        >
          {t("logout")}
        </Button>
      </CardFooter>
    </Card>
  );
};
